import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Layers,
  MessageSquare,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  UserCog,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import PillTabs from '../../../../components/common/PillTabs';
import PermissionsAssignmentModal from '../../common/Permissions/PermissionsAssignmentModal';
import { GOVERNED_MCP_TOOLS_ACCESS_ID, getCapabilityIcon, getCapabilityIconTone, splitAssetToolName } from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { emptyWorkspaceAssetCatalog, publishCatalogService, workspaceCatalogToSources } from '../publishCatalogService';
import { capabilityGroupIds, useGovernedMcp } from '../governedMcpStore';
import type {
  DataModelSource,
  KnowledgeBaseSource,
  McpCapabilityKind,
  PublishedCapability,
  ToolGroup,
  WorkspaceAssetCatalog,
  WorkflowSource,
} from '../governedMcpTypes';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import { fieldClass, primaryBtn } from '../components/GatewayUi';
import PublishCapabilityDrawer from '../drawers/PublishCapabilityDrawer';
import ToolGroupDrawer from '../drawers/ToolGroupDrawer';

type GroupFilter = 'all' | 'ungrouped' | string;

const CAPABILITY_TABS: { id: McpCapabilityKind; label: string; icon: LucideIcon }[] = [
  { id: 'tool', label: 'Tools', icon: Wrench },
  { id: 'resource', label: 'Resource', icon: FileText },
  { id: 'prompt', label: 'Prompts', icon: MessageSquare },
];

function capabilityOf(item: PublishedCapability): McpCapabilityKind {
  if (item.mcpCapability) return item.mcpCapability;
  const hasTool = item.mcpRepresentations?.includes('tool');
  const hasResource = item.mcpRepresentations?.includes('resource');
  if (hasResource && !hasTool) return 'resource';
  return 'tool';
}

function sourceTag(item: PublishedCapability): string {
  if (item.sourceKind === 'workflow') return 'Workflow';
  if (item.sourceKind === 'knowledge_base') return 'Knowledge';
  if (item.sourceKind === 'external_mcp') return 'External MCP';
  if (item.sourceKind === 'custom_text') return 'Custom';
  return 'Data model';
}

const PAGE_SIZE = 12;

function ToolCard({
  item,
  tag,
  isLight,
  onOpen,
  onEdit,
  onDelete,
  onPermissions,
}: {
  item: PublishedCapability;
  tag: string;
  isLight: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onPermissions: () => void;
}) {
  const ItemIcon = getCapabilityIcon(item.icon);
  const iconTone = getCapabilityIconTone(item.id);
  const inactive = item.status === 'disabled';

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className={`group relative flex flex-col rounded-2xl p-5 text-left cursor-pointer transition-all ${
        isLight
          ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04] hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] hover:-translate-y-0.5'
          : 'bg-neutral-900 ring-1 ring-white/10 hover:ring-white/20'
      } ${inactive ? 'opacity-70' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0 pr-16">
        <span
          className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
            isLight ? iconTone.light : iconTone.dark
          }`}
        >
          <ItemIcon size={18} />
        </span>
        <h3 className={`text-[15px] font-semibold tracking-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>
          {splitAssetToolName(item.mcpToolName).slug || item.mcpToolName}
        </h3>
      </div>

      <p className={`mt-3 text-sm leading-relaxed line-clamp-2 min-h-[2.5rem] ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        {item.description || 'No description provided.'}
      </p>

      <div className={`mt-4 pt-4 border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
            isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
          }`}
        >
          <Layers size={11} />
          {tag}
        </span>
      </div>

      <div
        className={`absolute top-3.5 right-3.5 flex items-center gap-0.5 rounded-xl p-0.5 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity ${
          isLight ? 'bg-white/90 shadow-sm ring-1 ring-black/5' : 'bg-neutral-800 ring-1 ring-white/10'
        }`}
      >
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-400 hover:text-white hover:bg-neutral-700'}`}
          aria-label="Edit"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-400 hover:text-white hover:bg-neutral-700'}`}
          aria-label="Permissions"
          onClick={(event) => {
            event.stopPropagation();
            onPermissions();
          }}
        >
          <UserCog size={14} />
        </button>
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-red-600 hover:bg-red-50' : 'text-neutral-400 hover:text-red-400 hover:bg-neutral-700'}`}
          aria-label="Remove"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </article>
  );
}

function Menu({
  open,
  onClose,
  children,
  isLight,
  align = 'right',
  root,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  isLight: boolean;
  align?: 'left' | 'right';
  root: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(`[data-menu-root="${root}"]`)) return;
      onClose();
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open, onClose, root]);
  if (!open) return null;
  return (
    <div
      className={`absolute z-20 mt-1.5 min-w-[200px] rounded-xl border py-1 shadow-xl ${align === 'left' ? 'left-0' : 'right-0'} ${
        isLight ? 'bg-white border-neutral-200 shadow-neutral-900/10' : 'bg-neutral-900 border-neutral-700'
      }`}
    >
      {children}
    </div>
  );
}

export default function ToolsTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, catalogs, savePublished, deletePublished, saveToolGroup, deleteToolGroup } = useGovernedMcp();
  const [query, setQuery] = useState('');
  const [capabilityFilter, setCapabilityFilter] = useState<McpCapabilityKind>('tool');
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const [sort, setSort] = useState<'name-asc' | 'name-desc'>('name-asc');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [tool, setTool] = useState<PublishedCapability | null | 'new'>(null);
  const [group, setGroup] = useState<ToolGroup | null | 'new'>(null);
  const [deleteTool, setDeleteTool] = useState<PublishedCapability | null>(null);
  const [deleteGroup, setDeleteGroup] = useState<ToolGroup | null>(null);
  const [permissionTool, setPermissionTool] = useState<PublishedCapability | null>(null);
  const [liveDataModels, setLiveDataModels] = useState<DataModelSource[]>([]);
  const [workspaceCatalog, setWorkspaceCatalog] = useState<WorkspaceAssetCatalog>(emptyWorkspaceAssetCatalog());
  const [liveWorkflows, setLiveWorkflows] = useState<WorkflowSource[]>([]);
  const [liveKnowledgeBases, setLiveKnowledgeBases] = useState<KnowledgeBaseSource[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogTick, setCatalogTick] = useState(0);

  useEffect(() => {
    if (tool === null) return;
    let cancelled = false;
    const load = async () => {
      setCatalogLoading(true);
      setCatalogError(null);
      logGovernedMcp('app', 'publish_catalogs_load');
      const errors: string[] = [];
      try {
        const workspace = await publishCatalogService.listWorkspaceAssets();
        if (!cancelled) {
          setWorkspaceCatalog(workspace);
          setLiveDataModels(workspaceCatalogToSources(workspace));
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load workspace assets';
        errors.push(message);
        logGovernedMcp('warning', 'publish_workspace_assets_unavailable', { message });
        if (!cancelled) {
          setWorkspaceCatalog(emptyWorkspaceAssetCatalog());
          setLiveDataModels([]);
        }
      }
      try {
        const rows = await publishCatalogService.listWorkflows();
        if (!cancelled) setLiveWorkflows(rows);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load workflows';
        errors.push(message);
        logGovernedMcp('warning', 'publish_workflows_unavailable', { message });
        if (!cancelled) setLiveWorkflows([]);
      }
      try {
        const rows = await publishCatalogService.listKnowledgeBases();
        if (!cancelled) setLiveKnowledgeBases(rows);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load knowledge bases';
        errors.push(message);
        logGovernedMcp('warning', 'publish_knowledge_bases_unavailable', { message });
        if (!cancelled) setLiveKnowledgeBases([]);
      }
      if (!cancelled) {
        setCatalogError(errors.length ? errors.join(' ') : null);
        setCatalogLoading(false);
        if (!errors.length) logGovernedMcp('app', 'publish_catalogs_load_ok');
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [tool, catalogTick]);

  const inKind = useMemo(
    () => state.published.filter((item) => capabilityOf(item) === capabilityFilter),
    [state.published, capabilityFilter]
  );
  const ungroupedCount = inKind.filter((item) => capabilityGroupIds(item).length === 0).length;

  const tools = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = inKind.filter((item) => {
      const ids = capabilityGroupIds(item);
      if (groupFilter === 'ungrouped' && ids.length) return false;
      if (groupFilter !== 'all' && groupFilter !== 'ungrouped' && !ids.includes(groupFilter)) return false;
      if (!q) return true;
      const groupNames = ids
        .map((id) => state.toolGroups.find((entry) => entry.id === id)?.name || '')
        .join(' ');
      return `${item.mcpToolName} ${item.description} ${item.sourceName} ${groupNames}`.toLowerCase().includes(q);
    });
    return [...filtered].sort((a, b) => {
      const left = splitAssetToolName(a.mcpToolName).slug || a.mcpToolName;
      const right = splitAssetToolName(b.mcpToolName).slug || b.mcpToolName;
      const cmp = left.localeCompare(right);
      return sort === 'name-desc' ? -cmp : cmp;
    });
  }, [inKind, state.toolGroups, query, groupFilter, sort]);

  useEffect(() => {
    setPage(1);
  }, [query, groupFilter, capabilityFilter, sort]);

  const promptComingSoon = capabilityFilter === 'prompt';
  const noun = capabilityFilter === 'resource' ? 'resource' : capabilityFilter === 'prompt' ? 'prompt' : 'tool';
  const selectedGroup = groupFilter !== 'all' && groupFilter !== 'ungrouped' ? state.toolGroups.find((item) => item.id === groupFilter) : null;
  const defaultGroupId = selectedGroup?.id || '';
  const pageCount = Math.max(1, Math.ceil(tools.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = tools.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const pageEnd = Math.min(safePage * PAGE_SIZE, tools.length);
  const pageItems = tools.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const filterLabel =
    groupFilter === 'all' ? 'All groups' : groupFilter === 'ungrouped' ? 'Ungrouped' : selectedGroup?.name || 'Filters';

  const openAdd = () => {
    if (promptComingSoon) return;
    logGovernedMcp('app', 'add_capability_open', { capability: capabilityFilter, groupId: defaultGroupId || null });
    setTool('new');
  };

  const menuItem = (active: boolean) =>
    `w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 ${
      active
        ? isLight
          ? 'bg-indigo-50 text-indigo-700'
          : 'bg-indigo-500/15 text-indigo-300'
        : isLight
          ? 'text-neutral-700 hover:bg-neutral-50'
          : 'text-neutral-300 hover:bg-neutral-800'
    }`;

  const pillBtn = (active?: boolean) =>
    `inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium border transition-colors ${
      active
        ? isLight
          ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
          : 'border-indigo-500/30 bg-indigo-500/15 text-indigo-300'
        : isLight
          ? 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
          : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
    }`;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PillTabs
          isLight={isLight}
          ariaLabel="Capability kinds"
          value={capabilityFilter}
          items={CAPABILITY_TABS.map((tab) => ({
            ...tab,
            count: state.published.filter((item) => capabilityOf(item) === tab.id).length,
          }))}
          onChange={(id) => {
            setCapabilityFilter(id);
            logGovernedMcp('app', 'capability_kind_tab', { capability: id });
          }}
        />
        <button
          type="button"
          className={`${primaryBtn()} rounded-full ${promptComingSoon ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={promptComingSoon}
          onClick={openAdd}
        >
          <Plus size={14} /> Add
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
          <input
            className={`${fieldClass(isLight)} pl-10 rounded-full ${isLight ? 'bg-white' : ''}`}
            placeholder={`Search ${noun}s…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="relative" data-menu-root="filters">
          <button type="button" className={pillBtn(groupFilter !== 'all')} onClick={() => setFiltersOpen((value) => !value)}>
            <SlidersHorizontal size={14} />
            {filterLabel}
            <ChevronDown size={13} />
          </button>
          <Menu open={filtersOpen} onClose={() => setFiltersOpen(false)} isLight={isLight} align="left" root="filters">
            <button
              type="button"
              className={menuItem(groupFilter === 'all')}
              onClick={() => {
                setGroupFilter('all');
                setFiltersOpen(false);
              }}
            >
              All
              <span className="tabular-nums text-[11px] opacity-60">{inKind.length}</span>
            </button>
            {state.toolGroups.map((item) => {
              const count = inKind.filter((toolItem) => capabilityGroupIds(toolItem).includes(item.id)).length;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={menuItem(groupFilter === item.id)}
                  onClick={() => {
                    setGroupFilter(item.id);
                    setFiltersOpen(false);
                  }}
                >
                  <span className="truncate">{item.name}</span>
                  <span className="tabular-nums text-[11px] opacity-60">{count}</span>
                </button>
              );
            })}
            <button
              type="button"
              className={menuItem(groupFilter === 'ungrouped')}
              onClick={() => {
                setGroupFilter('ungrouped');
                setFiltersOpen(false);
              }}
            >
              Ungrouped
              <span className="tabular-nums text-[11px] opacity-60">{ungroupedCount}</span>
            </button>
            <div className={`my-1 h-px ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`} />
            <button
              type="button"
              className={menuItem(false)}
              onClick={() => {
                setFiltersOpen(false);
                setGroup('new');
              }}
            >
              <span className="inline-flex items-center gap-1.5">
                <Plus size={13} /> New group
              </span>
            </button>
            {selectedGroup ? (
              <>
                <button
                  type="button"
                  className={menuItem(false)}
                  onClick={() => {
                    setFiltersOpen(false);
                    setGroup(selectedGroup);
                  }}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Pencil size={13} /> Edit group
                  </span>
                </button>
                <button
                  type="button"
                  className={menuItem(false)}
                  onClick={() => {
                    setFiltersOpen(false);
                    setDeleteGroup(selectedGroup);
                  }}
                >
                  <span className="inline-flex items-center gap-1.5 text-red-500">
                    <Trash2 size={13} /> Delete group
                  </span>
                </button>
              </>
            ) : null}
          </Menu>
        </div>
        <div className={`inline-flex items-center gap-1 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
          <span className="tabular-nums px-1">
            {pageStart}–{pageEnd} of {tools.length}
          </span>
          <button
            type="button"
            className={pillBtn()}
            disabled={safePage <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            className={pillBtn()}
            disabled={safePage >= pageCount}
            onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
            aria-label="Next page"
          >
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="relative ml-auto" data-menu-root="sort">
          <button type="button" className={pillBtn()} onClick={() => setSortOpen((value) => !value)}>
            {sort === 'name-desc' ? 'Name Z–A' : 'Name A–Z'}
            <ChevronDown size={13} />
          </button>
          <Menu open={sortOpen} onClose={() => setSortOpen(false)} isLight={isLight} root="sort">
            <button
              type="button"
              className={menuItem(sort === 'name-asc')}
              onClick={() => {
                setSort('name-asc');
                setSortOpen(false);
              }}
            >
              Name A–Z
            </button>
            <button
              type="button"
              className={menuItem(sort === 'name-desc')}
              onClick={() => {
                setSort('name-desc');
                setSortOpen(false);
              }}
            >
              Name Z–A
            </button>
          </Menu>
        </div>
      </div>

      {tools.length === 0 ? (
        <EmptyState
          isLight={isLight}
          title={promptComingSoon ? 'Prompts coming soon' : inKind.length === 0 ? `No ${noun}s published` : `No ${noun}s in this view`}
          description={
            promptComingSoon
              ? 'Prompt capabilities will be available in a later release.'
              : `Publish a ${noun} to make it available to governed MCP clients.`
          }
          action={
            promptComingSoon ? undefined : (
              <button type="button" className={`${primaryBtn()} rounded-full`} onClick={openAdd}>
                <Plus size={14} /> Add {noun}
              </button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pageItems.map((item) => {
            const groupName =
              capabilityGroupIds(item)
                .map((id) => state.toolGroups.find((entry) => entry.id === id)?.name)
                .filter(Boolean)
                .join(', ') || sourceTag(item);
            return (
              <ToolCard
                key={item.id}
                item={item}
                tag={groupName}
                isLight={isLight}
                onOpen={() => setTool(item)}
                onEdit={() => setTool(item)}
                onDelete={() => setDeleteTool(item)}
                onPermissions={() => {
                  setPermissionTool(item);
                  logGovernedMcp('app', 'capability_permissions_open', { id: item.id });
                }}
              />
            );
          })}
        </div>
      )}

      <PublishCapabilityDrawer
        open={tool !== null}
        initial={tool && tool !== 'new' ? tool : null}
        defaultGroupId={defaultGroupId}
        defaultCapability={capabilityFilter}
        clients={state.clients}
        dataModels={liveDataModels.length ? liveDataModels : catalogs.dataModels}
        workspaceCatalog={workspaceCatalog}
        workflows={liveWorkflows.length ? liveWorkflows : catalogs.workflows}
        knowledgeBases={liveKnowledgeBases}
        externalMcp={catalogs.externalMcp}
        toolGroups={state.toolGroups}
        catalogLoading={catalogLoading}
        catalogError={catalogError}
        published={state.published}
        onRetryCatalogs={() => setCatalogTick((value) => value + 1)}
        isLight={isLight}
        onClose={() => setTool(null)}
        onSave={(item) => {
          savePublished(item);
          setTool(null);
          const savedGroups = capabilityGroupIds(item);
          if (savedGroups.length && (groupFilter === 'all' || groupFilter === 'ungrouped' || !savedGroups.includes(groupFilter))) {
            setGroupFilter(savedGroups[0]);
          }
          onToast('success', `Capability “${item.mcpToolName}” saved`);
          logGovernedMcp('app', 'capability_saved_from_tab', { id: item.id, groupIds: savedGroups, capability: item.mcpCapability });
        }}
      />
      <ToolGroupDrawer
        open={group !== null}
        initial={group && group !== 'new' ? group : null}
        isLight={isLight}
        onClose={() => setGroup(null)}
        onSave={(item) => {
          saveToolGroup(item);
          setGroup(null);
          setGroupFilter(item.id);
          onToast('success', `Group “${item.name}” saved`);
        }}
      />
      {permissionTool ? (
        <PermissionsAssignmentModal
          onClose={() => setPermissionTool(null)}
          onSave={() => {}}
          entityType="entity"
          entityName={permissionTool.mcpToolName}
          module_id={GOVERNED_MCP_TOOLS_ACCESS_ID}
          object_id={permissionTool.id}
        />
      ) : null}
      <ConfirmDialog
        open={Boolean(deleteTool)}
        title="Remove capability?"
        description={deleteTool ? `Unpublish “${deleteTool.mcpToolName}”? Policies that bind this capability will no longer match it.` : ''}
        confirmLabel="Remove"
        danger
        isLight={isLight}
        onCancel={() => setDeleteTool(null)}
        onConfirm={() => {
          if (deleteTool) {
            deletePublished(deleteTool.id);
            onToast('success', 'Capability removed');
          }
          setDeleteTool(null);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleteGroup)}
        title="Delete group?"
        description={deleteGroup ? `Remove “${deleteGroup.name}”? Capabilities in this group become ungrouped. They are not deleted.` : ''}
        confirmLabel="Delete group"
        danger
        isLight={isLight}
        onCancel={() => setDeleteGroup(null)}
        onConfirm={() => {
          if (deleteGroup) {
            deleteToolGroup(deleteGroup.id);
            setGroupFilter('all');
            onToast('success', 'Group deleted');
          }
          setDeleteGroup(null);
        }}
      />
    </div>
  );
}
